import type { Dict } from "../lib/i18n";

// Keys are prefixed "report." — keep zh / en / nl in step (the other languages: locales/<lang>/report.json).
export default {
  zh: {
    "report.title": "家庭月报",
    "report.home": "家庭月报",
    "report.open": "查看本月家庭月报：钱、吃、健康一页看清",
    "report.prev": "上个月",
    "report.next": "下个月",
    "report.empty": "这个月还没有记录",
    "report.emptySub": "记账、拍小票、记饮食后，这里会把全家的一个月整理成一页。",
    "report.summary": "本月小结",
    "report.summaryHint": "请 AI 用几句话总结这个月，并给几条建议（只发送数字和商品名，不发送店名和备注）。",
    "report.summaryGet": "请 AI 写本月小结",
    "report.summaryLeft": "本月还可以请 AI 写 {n} 次",
    "report.money": "钱花在哪里",
    "report.moreThan": "比上个月多 {pct}%",
    "report.lessThan": "比上个月少 {pct}%",
    "report.lastMonth": "上个月：{amount}",
    "report.shopHealth": "买东西健不健康",
    "report.shopHealthLine": "本月 {trips} 次购物的健康分",
    "report.shopHigh": "买得最多的高糖、高盐或高脂食品：{names}",
    "report.budget": "本月预算",
    "report.deposits": "退瓶收回 {back}（押金付了 {paid}）",
    "report.toLedger": "打开账本分析",
    "report.prices": "比价",
    "report.overpaid": "同样的东西，在贵的店多花了 {amount}",
    "report.priceLine": "{name}：在 {store} 付 {paid}，{cheaper} 只要 {price}",
    "report.waste": "扔掉的食物",
    "report.wasteNone": "这个月没有扔掉食物，真棒！",
    "report.wasteTotal": "扔掉了 {n} 样，大约值 {amount}",
    "report.wasteAgain": "这些最近一再被扔掉，下次少买一点：{names}",
    "report.toPantry": "打开食材库",
    "report.goods": "买回家的东西",
    "report.goodsTotal": "买了 {n} 样东西，共 {amount}",
    "report.again": "半年内重复买的，是否必要？",
    "report.againLine": "{name}（{n} 次）",
    "report.toGoods": "打开物品库",
    "report.health": "吃得怎么样",
    "report.daysLogged": "这个月有 {n} 天记了饮食（共 {days} 天）",
    "report.avgKcal": "平均每天热量（千卡）",
    "report.avgKcalOnly": "平均每天热量：{v} 千卡",
    "report.avgSugar": "平均每天糖",
    "report.avgSalt": "平均每天盐",
    "report.weight": "体重变化：{kg} 公斤",
    "report.exercise": "运动 {min} 分钟，消耗约 {kcal} 千卡",
    "report.toMeals": "打开饮食",
    "report.excel": "导出 Excel（全家月报）",
  },
  en: {
    "report.title": "Family report",
    "report.home": "Family report",
    "report.open": "See this month's family report: money, food and health on one page",
    "report.prev": "Previous month",
    "report.next": "Next month",
    "report.empty": "Nothing recorded this month yet",
    "report.emptySub": "Once you note spending, scan receipts and log meals, the family's month is put on one page here.",
    "report.summary": "This month in short",
    "report.summaryHint": "Ask the AI to sum up the month in a few sentences with a few tips (only figures and product names are sent — no shop names or notes).",
    "report.summaryGet": "Ask the AI for a summary",
    "report.summaryLeft": "{n} AI summaries left this month",
    "report.money": "Where the money went",
    "report.moreThan": "{pct}% more than last month",
    "report.lessThan": "{pct}% less than last month",
    "report.lastMonth": "last month: {amount}",
    "report.shopHealth": "How healthy the shopping was",
    "report.shopHealthLine": "Health score of {trips} shopping trips",
    "report.shopHigh": "Most bought food high in sugar, salt or fat: {names}",
    "report.budget": "This month's budget",
    "report.deposits": "Bottle returns {back} (deposits paid {paid})",
    "report.toLedger": "Open budget insights",
    "report.prices": "Prices",
    "report.overpaid": "Paid {amount} more for the same things at dearer shops",
    "report.priceLine": "{name}: {paid} at {store}, only {price} at {cheaper}",
    "report.waste": "Food thrown away",
    "report.wasteNone": "No food thrown away this month — well done!",
    "report.wasteTotal": "{n} things thrown away, worth about {amount}",
    "report.wasteAgain": "Thrown away again and again lately — buy less next time: {names}",
    "report.toPantry": "Open the pantry",
    "report.goods": "Things bought to keep",
    "report.goodsTotal": "{n} things bought, {amount} in total",
    "report.again": "Bought again within half a year — needed?",
    "report.againLine": "{name} ({n}×)",
    "report.toGoods": "Open home goods",
    "report.health": "How everyone ate",
    "report.daysLogged": "Meals logged on {n} of {days} days",
    "report.avgKcal": "Average calories a day (kcal)",
    "report.avgKcalOnly": "Average calories a day: {v} kcal",
    "report.avgSugar": "Average sugar a day",
    "report.avgSalt": "Average salt a day",
    "report.weight": "Weight change: {kg} kg",
    "report.exercise": "Exercise {min} min, about {kcal} kcal",
    "report.toMeals": "Open meals",
    "report.excel": "Export to Excel (family report)",
  },
  nl: {
    "report.title": "Gezinsrapport",
    "report.home": "Gezinsrapport",
    "report.open": "Bekijk het gezinsrapport van deze maand: geld, eten en gezondheid op één pagina",
    "report.prev": "Vorige maand",
    "report.next": "Volgende maand",
    "report.empty": "Deze maand is er nog niets genoteerd",
    "report.emptySub": "Zodra je uitgaven noteert, bonnen scant en eten bijhoudt, zet NutriCart de maand van het gezin hier op één pagina.",
    "report.summary": "Deze maand in het kort",
    "report.summaryHint": "Laat de AI de maand in een paar zinnen samenvatten met een paar tips (alleen bedragen en productnamen gaan mee — geen winkelnamen of notities).",
    "report.summaryGet": "Vraag de AI om een samenvatting",
    "report.summaryLeft": "Nog {n} AI-samenvattingen deze maand",
    "report.money": "Waar het geld heen ging",
    "report.moreThan": "{pct}% meer dan vorige maand",
    "report.lessThan": "{pct}% minder dan vorige maand",
    "report.lastMonth": "vorige maand: {amount}",
    "report.shopHealth": "Hoe gezond er is ingekocht",
    "report.shopHealthLine": "Gezondheidsscore van {trips} keer boodschappen",
    "report.shopHigh": "Meest gekocht met veel suiker, zout of vet: {names}",
    "report.budget": "Budget van deze maand",
    "report.deposits": "Statiegeld terug {back} (betaald {paid})",
    "report.toLedger": "Inzicht in het huishoudboekje",
    "report.prices": "Prijzen",
    "report.overpaid": "{amount} meer betaald voor dezelfde dingen in duurdere winkels",
    "report.priceLine": "{name}: {paid} bij {store}, maar {price} bij {cheaper}",
    "report.waste": "Weggegooid eten",
    "report.wasteNone": "Deze maand niets weggegooid — goed zo!",
    "report.wasteTotal": "{n} dingen weggegooid, ongeveer {amount} waard",
    "report.wasteAgain": "Steeds weer weggegooid — koop de volgende keer minder: {names}",
    "report.toPantry": "Naar de voorraad",
    "report.goods": "Gekochte spullen",
    "report.goodsTotal": "{n} spullen gekocht, samen {amount}",
    "report.again": "Binnen een halfjaar opnieuw gekocht — nodig?",
    "report.againLine": "{name} ({n}×)",
    "report.toGoods": "Naar spullen in huis",
    "report.health": "Hoe er gegeten is",
    "report.daysLogged": "Eten genoteerd op {n} van de {days} dagen",
    "report.avgKcal": "Gemiddeld per dag (kcal)",
    "report.avgKcalOnly": "Gemiddeld per dag: {v} kcal",
    "report.avgSugar": "Gemiddeld suiker per dag",
    "report.avgSalt": "Gemiddeld zout per dag",
    "report.weight": "Gewichtsverandering: {kg} kg",
    "report.exercise": "Bewogen {min} min, ongeveer {kcal} kcal",
    "report.toMeals": "Naar eten",
    "report.excel": "Exporteren naar Excel (gezinsrapport)",
  },
} satisfies Dict;
